import React, { useContext } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import LeadCard from './LeadCard';
import { LanguageContext } from '../../../../Context';

/**
 * PipelineColumn - A single stage column in the kanban pipeline.
 * Acts as a droppable zone and contains sortable lead cards.
 *
 * @param {Object} props
 * @param {Object} props.stage - Stage config { id, label, color, bg, headerBg }
 * @param {Object[]} props.leads - Array of leads in this stage
 * @param {Function} props.onLeadClick - Callback when a lead card is clicked
 * @param {Function} props.onAddClick - Callback to add a new lead in this stage
 * @returns {JSX.Element}
 * @author Forge, opencode glm-5.1
 * @version 1.1
 * @since 2026-04-15
 */
export default function PipelineColumn({ stage, leads, onLeadClick, onAddClick }) {
  const { l, language } = useContext(LanguageContext);

  const t = (key, fallback) => {
    try { return l[key]?.[language] || fallback || key; } catch { return fallback || key; }
  };

  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
    data: { stage: stage.id },
  });

  return (
    <div
      className="flex flex-col flex-1 min-w-[230px] rounded-[12px] overflow-hidden"
      style={{
        backgroundColor: isOver ? stage.bg : '#f8fafc',
        transition: 'background-color 150ms ease-out',
        border: isOver ? `2px dashed ${stage.color}` : '2px solid transparent',
      }}
    >
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ backgroundColor: stage.headerBg }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: stage.color, boxShadow: `0 0 6px ${stage.color}60` }}
          />
          <h3 className="text-sm font-bold text-white tracking-wide">{stage.label}</h3>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/20 text-white/90">
            {leads.length}
          </span>
        </div>
        <button
          onClick={() => onAddClick(stage.id)}
          className="w-6 h-6 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-colors duration-150 cursor-pointer"
          title={`${t('pipeline.addLeadTo', 'Add lead to')} ${stage.label}`}
        >
          <Plus size={16} />
        </button>
      </div>

      <div
        ref={setNodeRef}
        className="flex-1 p-2 space-y-2 overflow-y-auto min-h-[200px]"
        style={{ maxHeight: 'calc(92vh - 200px)' }}
      >
        <SortableContext
          items={leads.map(l => l.id || l._id)}
          strategy={verticalListSortingStrategy}
        >
          {leads.map(lead => (
            <LeadCard
              key={lead.id || lead._id}
              lead={lead}
              onClick={onLeadClick}
              stage={stage}
            />
          ))}
        </SortableContext>

        {leads.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-slate-400">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mb-2"
              style={{ backgroundColor: stage.bg }}
            >
              <Plus size={20} style={{ color: stage.color }} />
            </div>
            <p className="text-xs">{t('pipeline.dropLeadsHere', 'Drop leads here')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
